import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import type { WebSocketMessage } from '../types'

/**
 * Socket.IO-based WebSocket service for SocketCloud console
 * Provides resilience against Chrome offline detection bug
 */
export class SocketIOWebSocketService {
  private socket: Socket | null = null;
  private url: string;
  private connected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private messageQueue: WebSocketMessage[] = [];
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map();

  constructor(url?: string) {
    console.log('[SOCKET.IO] Constructor start');
    
    // Determine Socket.IO URL
    const wsUrl = import.meta.env.VITE_WS_URL;
    
    if (url) {
      this.url = url;
    } else if (wsUrl) {
      this.url = wsUrl;
    } else {
      // Default to current origin (goes through nginx proxy)
      this.url = window.location.origin;
    }
    
    console.log('[SOCKET.IO] Socket.IO URL:', this.url);
  }

  /**
   * Connect to Socket.IO server
   */
  connect(): Promise<void> {
    console.log('[SOCKET.IO] Connect called');
    
    return new Promise((resolve, reject) => {
      try {
        console.log('[SOCKET.IO] Creating Socket.IO connection to:', this.url);
        const startTime = performance.now();
        
        this.socket = io(this.url, {
          path: '/socket.io/',
          transports: ['websocket', 'polling'], // Fallback to polling if WebSocket fails
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          reconnectionDelayMax: 5000,
          // Force new connection to avoid Chrome caching issues
          forceNew: true,
          // Cross-origin insurance for the `sc-auth-token` cookie. In the
          // DEPLOYED topology this flag changes nothing: VITE_WS_URL is unset,
          // so `this.url` falls back to window.location.origin, and the LB
          // routes /socket.io/* on that same host to socket-cloud-api. Neither
          // transport needs it same-origin -- engine.io-client 4.8.3's
          // websocket.js never reads withCredentials (0 occurrences), and its
          // polling transport is polling-xhr, where `xhr.withCredentials =
          // false` still sends cookies because that flag governs cross-origin
          // only. It matters if VITE_WS_URL is ever pointed at another origin
          // (the .env.local.example LAN-dev shape), and it is set so that day
          // is not an outage.
          withCredentials: true,
        });
        
        this.socket.on('connect', () => {
          const connectTime = performance.now() - startTime;
          console.log('[SOCKET.IO] CONNECTED!');
          console.log('[SOCKET.IO] Connection established in', connectTime.toFixed(2), 'ms');
          console.log('[SOCKET.IO] Socket ID:', this.socket?.id);
          console.log('[SOCKET.IO] Transport:', this.socket?.io.engine.transport.name);
          
          this.connected = true;
          this.reconnectAttempts = 0;
          
          // Send connected event
          this.routeMessage('connected', { socketId: this.socket?.id });
          
          // Flush queued messages
          this.flushMessageQueue();
          
          resolve();
        });
        
        // The server pushes each event under its own Socket.IO event name,
        // with the payload as its single argument (socket-cloud.openapi.yaml
        // SocketEventName; operator ruling 2026-10-14, Card 11). onAny sees
        // every server packet and none of the reserved connect/disconnect
        // events, which the handlers here route under their own names.
        this.socket.onAny((event: string, data: unknown) => {
          this.routeMessage(event, data);
        });
        
        this.socket.on('connect_error', (error) => {
          const errorTime = performance.now() - startTime;
          console.error('[SOCKET.IO] Connection error after', errorTime.toFixed(2), 'ms:', error.message);

          // The transports above already include polling, so a connect_error
          // leaves no fallback to wait for: the caller learns the connection
          // failed. The Manager keeps reconnecting, and a later connect is
          // routed as 'connected'. Once connect() has settled, this is a no-op.
          reject(error);
        });
        
        this.socket.on('disconnect', (reason) => {
          console.log('[SOCKET.IO] Disconnected:', reason);
          this.connected = false;
          this.routeMessage('disconnected', { reason });
          
          // Handle Chrome offline detection
          if (reason === 'transport close' || reason === 'transport error') {
            console.log('[SOCKET.IO] Possible Chrome offline detection, will attempt reconnect');
          }
        });
        
        // Reconnection belongs to the Manager: socket.io-client 4 emits these
        // events on socket.io, never on the Socket.
        this.socket.io.on('reconnect_attempt', (attempt) => {
          console.log('[SOCKET.IO] Reconnection attempt', attempt);
          this.reconnectAttempts = attempt;
        });

        this.socket.io.on('reconnect', (attemptNumber) => {
          console.log('[SOCKET.IO] Reconnected after', attemptNumber, 'attempts');
          this.connected = true;
          this.reconnectAttempts = 0;
          this.routeMessage('reconnected', { attempts: attemptNumber });
        });

        this.socket.io.on('reconnect_failed', () => {
          console.error('[SOCKET.IO] Reconnection failed after', this.maxReconnectAttempts, 'attempts');
          this.routeMessage('reconnect_failed', {});
        });
        
        // Socket.IO specific events
        this.socket.on('ping', () => {
          console.log('[SOCKET.IO] Ping received');
        });
        
        this.socket.on('pong', (latency) => {
          console.log('[SOCKET.IO] Pong received, latency:', latency, 'ms');
        });
        
      } catch (error) {
        console.error('[SOCKET.IO] Error creating connection:', error);
        reject(error);
      }
    });
  }

  /**
   * Send a message through Socket.IO
   */
  send(message: WebSocketMessage): void {
    if (!this.socket) {
      console.warn('[SOCKET.IO] Cannot send message: no socket connection');
      this.messageQueue.push(message);
      return;
    }
    
    if (!this.connected) {
      console.log('[SOCKET.IO] Queueing message (not connected):', message.type);
      this.messageQueue.push(message);
      return;
    }
    
    console.log('[SOCKET.IO] Sending message:', message.type);
    this.socket.emit('message', message);
  }

  /**
   * Subscribe to specific event types
   */
  subscribe(event: string, handler: (data: any) => void): () => void {
    console.log('[SOCKET.IO] Subscribing to event:', event);
    
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    
    this.eventHandlers.get(event)!.add(handler);
    
    // Return unsubscribe function
    return () => {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.eventHandlers.delete(event);
        }
      }
    };
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect(): void {
    console.log('[SOCKET.IO] Disconnect called');
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connected && this.socket?.connected === true;
  }

  /**
   * Join a room (Socket.IO specific)
   */
  joinRoom(room: string): void {
    if (this.socket && this.connected) {
      console.log('[SOCKET.IO] Joining room:', room);
      this.socket.emit('join-tenant', room);
    }
  }

  /**
   * Leave a room (Socket.IO specific)
   */
  leaveRoom(room: string): void {
    if (this.socket && this.connected) {
      console.log('[SOCKET.IO] Leaving room:', room);
      this.socket.emit('leave-tenant', room);
    }
  }

  /**
   * Route messages to subscribed handlers
   */
  private routeMessage(type: string, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers && handlers.size > 0) {
      console.log(`[SOCKET.IO] Routing ${type} to ${handlers.size} handlers`);
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[SOCKET.IO] Error in ${type} handler:`, error);
        }
      });
    }
  }

  /**
   * Flush queued messages
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length > 0) {
      console.log(`[SOCKET.IO] Flushing ${this.messageQueue.length} queued messages`);
      const queue = [...this.messageQueue];
      this.messageQueue = [];

      queue.forEach(message => {
        this.send(message);
      });
    }
  }

  /**
   * Get connection statistics
   */
  getStats(): {
    connected: boolean;
    reconnectAttempts: number;
    queuedMessages: number;
    activeSubscriptions: number;
  } {
    let totalHandlers = 0;
    this.eventHandlers.forEach(handlers => {
      totalHandlers += handlers.size;
    });

    return {
      connected: this.connected,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: this.messageQueue.length,
      activeSubscriptions: totalHandlers,
    };
  }
}

// Export singleton instance
export const socketIOService = new SocketIOWebSocketService();